/* eslint-disable camelcase */
/* eslint-disable react/prop-types */
/** @jsxImportSource theme-ui */
import { jsx } from 'theme-ui';
import React from 'react';
import { Flex, Box } from '@theme-ui/components';
import { navigate, graphql } from 'gatsby';
import { Container } from '../../components/organisms';
import { Layout, SEO } from '../../components';
import { MDXProvider } from '@mdx-js/react';
import { MDXRenderer } from 'gatsby-plugin-mdx';
import {
	StatesProfTaxSlab,
	ProfTaxStateGrid,
	ShopsStateGrid,
	StatesShopInfo,
	LWFStateGrid,
	LWFStateInfo,
	StateLWFReckoner,
	StatewiseLWFInfo,
	CodeOnWagesStateGrid,
	IndustrialRelationCodeStateGrid,
	CodeOnSocialSecurityStateGrid,
	OSHWCCodeStateGrid,
	Note,
	DocImage,
	ReadMore,
} from '../../components/shortcodes';

import {
	SideWrapper,
	PillarList,
	CompliancesList,
	RichContent,
	PageHeading,
	ActsList,
	NotificationsList,
	CalendarSideSection,
} from '../../components/wiki';
import { TOCList } from '../../components/utils';
import styled from '@emotion/styled';
import { colors, typography } from '../../styles/theme';
import { ActionBanner } from '../../components/molecules';
import { Button } from '../../components/atoms';

const shortcodes = {
	StatesProfTaxSlab,
	ProfTaxStateGrid,
	ShopsStateGrid,
	StatesShopInfo,
	LWFStateGrid,
	StatewiseLWFInfo,
	CodeOnWagesStateGrid,
	IndustrialRelationCodeStateGrid,
	CodeOnSocialSecurityStateGrid,
	OSHWCCodeStateGrid,
	Note,
	DocImage,
	ReadMore,
};

const MdxContent = ({ body, shortcodes }) => {
	return (
		<MDXProvider components={shortcodes}>
			<MDXRenderer>{body}</MDXRenderer>
		</MDXProvider>
	);
};

const WikiStateActsTemplate = ({
	data: {
		datoCmsAct,
		compliances,
		related_acts,
		notifications,
		pillar_notifications,
	},
	location: { href, search },
}) => {
	const {
		name,
		bodyNode: { childMdx },
		headerNode,
		footerNode,
		description,
		applicability,
		references,
		state,
		industry,
		pillar,
		showToc,
	} = datoCmsAct;

	const crumbs = [];

	if (pillar) {
		crumbs.push({
			crumbLabel: pillar.shortName,
			path: `/wiki/acts/${pillar.slug}/`,
		});
	}

	if (state) {
		crumbs.push({
			crumbLabel: state.name,
			path: `/wiki/acts/st-${state.slug}/`,
		});
	}
	if (industry) {
		crumbs.push({
			crumbLabel: industry.name,
			path: `/wiki/acts/in-${industry.slug}/`,
		});
	}

	const Compliances = () => (
		<CompliancesList
			compliances={compliances.nodes}
			heading={null}
		/>
	);
	const localShortCodes = { Compliances, ...shortcodes };
	const { calendar: hasCalendar } = state;
	const showLWFReadyReckoner =
		pillar && pillar.shortName === 'Labour Welfare Fund Act';
	const moreNotifications = pillar_notifications?.nodes?.length
		? pillar_notifications.nodes.concat(notifications?.nodes)
		: notifications?.nodes;
    const pageName = "Wiki_Act_Page";
	const demoBlockId = "/demo/?gc_source=wiki-act&ac_form_tag=AF_DEMO_WIKI_ACT";

	return (
		<Layout demoBlockId={demoBlockId}>
			<PageHeading
				title={name}
				crumbs={crumbs}
			/>
			<Box sx={{ px: ["1rem", "1rem", "2rem", "2rem"], mb: ["4rem","4rem","7rem","7rem"] }}>
				<Flex sx={{ mt: '3rem', flexDirection: ['column', 'column', 'column', 'row'], alignItems: ["center", "center", "center", "flex-start"], justifyContent: "center", gap: ["3rem", "3rem", "3rem", "7rem"], }}>
					<Container variant="pillar" p={[0, 0, 0, 0]} m={[0, 0, 0, 0]}>
					<StyledBox
						sx={{
							width: "100%",
							overflow: "auto",
							border: "0px solid green",
						}}
					>
						{childMdx && childMdx.rawBody !== "" ? (
							<Box>
								{headerNode ? (
									<MdxContent body={headerNode.childMdx.body} />
								) : null}

								<MdxContent
									body={childMdx.body}
									shortcodes={localShortCodes}
								/>
								{footerNode ? (
									<MdxContent body={footerNode.childMdx.body} />
								) : null}
							</Box>
						) : (
							<>
								<RichContent body={description} />
								{showLWFReadyReckoner ? (
									<StateLWFReckoner state={state} />
								) : null}
								<CompliancesList compliances={compliances.nodes} />
								<RichContent
									title="Applicability"
									body={applicability}
								/>
								<RichContent
									title="References"
									body={references}
								/>
							</>
						)}
					</StyledBox>
					</Container>
					<Flex
						sx={{
							flexDirection: "column",
							width: '17rem',
							gap: "3rem"
						}}
					>
						{hasCalendar ? <CalendarSideSection slug={state.slug} /> : null}
						{showToc ? (
							<TOCList headings={childMdx.headings} />
						) : (
							related_acts?.nodes?.length > 0 && (
								<ActsList
									shortName
									nodes={related_acts.nodes}
								/>
							)
						)}
					</Flex>
				</Flex>
				{moreNotifications.length > 0 && (
					<NotificationsList
						nodes={moreNotifications}
						title="More Notifications"
					/>
				)}
			</Box>
			<ActionBanner
				variant="BodyBannerV3"
				theme="primary"
				title="Discuss business needs, find out pricing or get a demo. We would love to talk."
				customStyles={{ mx: ["1.063rem", "1.063rem", "2rem", "3.125rem"] }}
			>
				<Button
					variant="outlined"
					size="large"
					id={`${pageName?.toLowerCase()}_request_demo_in_btn`}
					onClick={() =>
						navigate("/demo?gc_source=wiki-act&ac_form_tag=AF_DEMO_WIKI_ACT")
					}
				>
					Request a Demo
				</Button>
			</ActionBanner> 
		</Layout>
	);
};

const StyledBox = styled(Box)`
	table {
		font-size: ${typography.textMd400.fontSize};
		font-style: ${typography.textMd400.fontStyle};
		font-weight: ${typography.textMd400.fontWeight};
		font-family: ${typography.textMd400.fontFamily};
		line-height: ${typography.textMd400.lineHeight};
		border-spacing: 0;
		border-collapse: collapse;
		width: 100%;
		height: 100%;
		td {
			padding: 1.5rem;
			border: 1px solid ${colors.neutral200};
			text-align: left;
		}
		@media (max-width: 600px) {
			td {
				padding: 0.5rem;
			}
		}
	}
`;

export default WikiStateActsTemplate;

export const wikiActsData = graphql`
	query ActInfo($actId: String!, $stateId: String!) {
		datoCmsAct(id: { eq: $actId }) {
			id
			name
			slug
			shortName
			seo {
				title
				description
				image {
					url
				}
			}
			state {
				slug
				name
				calendar
			}
			industry {
				slug
				name
			}
			headerNode {
				childMdx {
					body
				}
			}
			bodyNode {
				childMdx {
					body
					rawBody
					headings {
						depth
						value
					}
				}
			}
			footerNode {
				childMdx {
					body
				}
			}
			description
			applicability
			code
			displayOrder
			references
			remarks
			pillar {
				shortName
				slug
			}
			showToc
		}
		compliances: allDatoCmsCompliance(
			filter: { act: { id: { eq: $actId } } }
			sort: { displayOrder: ASC }
		) {
			nodes {
				id
				name
				slug
			}
		}

		notifications: allDatoCmsNotification(
			limit: 50
			filter: { act: { id: { eq: $actId } } }
			sort: { date: DESC }
		) {
			nodes {
				id
				date
				title
				slug
				summary
			}
		}

		pillar_notifications: allDatoCmsNotification(
			limit: 50
			filter: { act: { pillar: { id: { eq: $actId } } } }
			sort: { date: DESC }
		) {
			nodes {
				id
				date
				title
				slug
				summary
			}
		}

		related_acts: allDatoCmsAct(
			filter: { state: { id: { eq: $stateId } }, id: { ne: $actId } }
			sort: { shortName: ASC }
		) {
			nodes {
				name
				shortName
				slug
			}
		}
	}
`;

export const Head = ({ data: { datoCmsAct }, location: { pathname } }) => (
	<SEO
		datoSeo={datoCmsAct.seo}
		pathname={pathname}
		article
	/>
);
